import classnames from 'classnames'
import React, { forwardRef, useMemo, useRef } from 'react'
import { useTheme } from '@opallabs/hooks'
import { Position } from '../../gem'
import { ButtonVariant } from '../ButtonVariant'
import { Icon } from '../Icon'
import { selectors, state as stateStyles, styles } from './styles'
import {
  ButtonIconPosition,
  ButtonProps,
  Size as ButtonSize,
  propDefaults,
  TypeAttribute,
} from './types'

export {
  ButtonIconPosition,
  ButtonSize,
  TypeAttribute,
  selectors as buttonSelectors,
  stateStyles as buttonState,
}

export type { ButtonProps }

export const Button = React.memo(
  forwardRef(
    (
      props: React.PropsWithChildren<ButtonProps>,
      ref?: React.ComponentPropsWithRef<'button'>['ref']
    ) => {
      const {
        ariaControls,
        ariaExpanded,
        ariaHasPopup,
        ariaLabel,
        ariaLabelledBy,
        ariaLive,
        ariaPressed,
        ariaSelected,
        buttonText,
        children,
        childrenPosition = propDefaults.childrenPosition,
        className,
        disabled = propDefaults.disabled,
        displayMenuIndicator = propDefaults.displayMenuIndicator,
        hideButtonText = propDefaults.hideButtonText,
        icon,
        iconPosition,
        menuIndicator,
        menuIndicatorPosition,
        name,
        onClick,
        onKeyDown,
        overrideIconStyles = {},
        overrideStyles = {},
        role,
        showAsActiveWhenPressed = propDefaults.showAsActiveWhenPressed,
        size = propDefaults.size,
        styleOverrides,
        tabIndex = propDefaults.tabIndex,
        theme: propTheme,
        typeAttribute = propDefaults.typeAttribute,
        variant = propDefaults.variant,
        ...otherProps
      } = props

      const providedTheme = useTheme()
      const theme = propTheme ?? providedTheme

      const triggeredByKeypress = useRef(false)

      const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          triggeredByKeypress.current = true
        }

        onKeyDown?.(e)
      }
      const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick) onClick(e, triggeredByKeypress.current)
        triggeredByKeypress.current = false
      }

      const buttonIcon = icon ? (
        <span
          className="lapidary-button-icon"
          css={[
            styles.icon({ hideButtonText, iconPosition }),
            overrideIconStyles,
            styleOverrides?.icon,
          ]}
        >
          {icon}
        </span>
      ) : null

      const menuIconPosition = menuIndicatorPosition ?? iconPosition
      const renderedMenuIndicator = displayMenuIndicator
        ? menuIndicator || (
            <Icon
              ariaMode="hidden"
              glyph="gem-chevron-s"
              css={{
                ...styleOverrides?.menuIndicator,
                marginInlineStart: 6, // + 4 pixels of implicit space
                order: menuIconPosition === ButtonIconPosition.leading ? -1 : 2,
              }}
            />
          )
        : null

      const text = buttonText ? (
        <span
          className={classnames(selectors.buttonText, {
            'sr-only': hideButtonText,
          })}
          css={[
            styles.text({
              childrenPosition,
              hasChildren: Boolean(children),
              hasIcon: Boolean(icon),
            }),
            styleOverrides?.text,
          ]}
        >
          {buttonText}
        </span>
      ) : null

      const iconOnly = useMemo(
        () =>
          (Boolean(icon) || Boolean(displayMenuIndicator)) &&
          (hideButtonText || !buttonText?.trim()),
        [buttonText, displayMenuIndicator, hideButtonText, icon]
      )

      return (
        <ButtonVariant
          aria-controls={ariaControls}
          aria-expanded={ariaExpanded}
          aria-haspopup={ariaHasPopup}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-live={ariaLive}
          aria-pressed={ariaPressed}
          aria-selected={ariaSelected}
          className={classnames(
            'lapidary-button',
            styles.selectors.root,
            className,
            {
              'toggle-button': showAsActiveWhenPressed,
              [stateStyles.iconOnly]: iconOnly,
            }
          )}
          css={[
            styles.button({ displayMenuIndicator, theme }),
            styles.size[size],
            disabled ? styles.disabled : null,
            overrideStyles,
            styleOverrides?.button,
          ]}
          disabled={disabled}
          name={name}
          onClick={handleOnClick}
          onKeyDown={handleKeyDown}
          ref={ref}
          role={role}
          tabIndex={tabIndex}
          theme={theme}
          type={typeAttribute}
          variant={variant}
          {...otherProps}
        >
          <span
            className={classnames(selectors.buttonContents)}
            css={styles.children({
              iconPosition: iconPosition || ButtonIconPosition.trailing,
            })}
          >
            {childrenPosition === Position.Start && children}

            {text}
            {buttonIcon}

            {childrenPosition === Position.End && children}
          </span>

          {renderedMenuIndicator}
        </ButtonVariant>
      )
    }
  )
)
Button.displayName = 'Lapidary: Button'
